import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';
import { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useLabelledFields as useActionLabelledFields } from 'src/pages/actions/useLabelledFields';
import type { LabelledIdArray } from 'src/rbac/types';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { getFriendlyId } from '@/utils/friendlyId';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { UNRATED } from '../../gigawidget/types';
import { dateRangeFilter } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';
import {
  getActionOverdueLabel,
  ownersField,
  prefixedLabel,
  severitySortedField,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type RiskActionRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskFriendlyId: string;
  RiskTitle: string;
  RiskTier: string;
  RiskStatus: string;
  RiskTreatment: string;
  ActionId: string;
  ActionFriendlyId: string;
  ActionTitle: string;
  // Labelled status excluding the overdue fold — overdue is its own column/getter.
  ActionStatus: string;
  // Labelled priority for table rendering ("High", "Urgent", ...).
  ActionPriority: string;
  // Raw priority value (1 = Urgent ... 4 = Low) — used as categoryGetter key
  // so charts sort by severity rather than alphabetically (RSP-5303).
  ActionPriorityValue: null | number;
  ActionDateDue: string;
  ActionOwners: LabelledIdArray;
  ActionOverdue: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc, _variables, entityIds) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({
      entityIds,
    });
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<RiskActionRow>
  ) => {
    const fields = useMemo<TableFields<RiskActionRow>>(
      () => ({
        RiskFriendlyId: simpleField(
          prefixedLabel('risk_one', i18n.t('columns.id'))
        ),
        RiskTitle: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_name'))
        ),
        RiskTier: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier'))
        ),
        RiskStatus: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_status'))
        ),
        RiskTreatment: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment'))
        ),
        ActionFriendlyId: simpleField(
          prefixedLabel('action_one', i18n.t('columns.id'))
        ),
        ActionTitle: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.title'))
        ),
        ActionStatus: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.status'))
        ),
        ActionPriority: severitySortedField<RiskActionRow>(
          prefixedLabel('action_one', i18n.t('actions.columns.priority')),
          (item) => item.ActionPriorityValue
        ),
        ActionDateDue: dateColumnFromConfig<RiskActionRow>({
          header: {
            header: prefixedLabel(
              'action_one',
              i18n.t('actions.columns.due_date')
            ),
          },
          dateField: 'ActionDateDue',
        }),
        ActionOwners: ownersField(
          prefixedLabel('action_one', i18n.t('actions.columns.owner')),
          (item) => item.ActionOwners
        ),
        ActionOverdue: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.overdue'))
        ),
      }),
      []
    );

    const actionsLabelled = useActionLabelledFields(data?.action);
    const { getLabel: getActionStatusLabel } = useRating('action_status');
    const { getByValue: commonLookup } = useCommonLookupLazy();
    const { data: riskData, loading: risksLoading } = useGetRisksFlat({
      queryArgs: {},
    });

    const joinedData = useMemo<RiskActionRow[] | undefined>(() => {
      if (!actionsLabelled || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return actionsLabelled.flatMap((action) =>
        action.parents
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              RiskId: parent.parent!.Id,
              RiskFriendlyId: risk?.SequentialId
                ? getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)
                : '',
              RiskTitle: risk?.Title ?? parent.risk?.Title ?? '',
              RiskTier: commonLookup('tiers', risk?.Tier)?.label ?? '',
              RiskStatus: commonLookup('statuses', risk?.Status)?.label ?? '',
              RiskTreatment:
                commonLookup('treatments', risk?.Treatment)?.label ?? '',
              ActionId: action.Id,
              ActionFriendlyId: action.SequentialIdLabel ?? '',
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status
                ? (getActionStatusLabel(action.Status) ?? '')
                : '',
              ActionPriority: action.PriorityLabelled ?? '',
              ActionPriorityValue: action.Priority ?? null,
              ActionDateDue: action.DateDue ?? '',
              ActionOwners: action.allOwners,
              ActionOverdue: getActionOverdueLabel(action),
            };
          })
      );
    }, [actionsLabelled, riskData?.risk, commonLookup, getActionStatusLabel]);

    const tableProps = useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_action_other'),
      options
    );

    // The join stays undefined until the secondary risks query lands —
    // report that window as loading rather than "no data" (RSP-6539).
    return { ...tableProps, isEnrichmentLoading: risksLoading };
  },
  entityNamePlural: 'risk_action_other',
  entityNameSingular: 'risk_action_one',
  fields: 'riskActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskFriendlyId',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('columns.id')),
      categoryGetter: (item: RiskActionRow) => item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_name')),
      categoryGetter: (item: RiskActionRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier')),
      categoryGetter: (item: RiskActionRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_status')),
      categoryGetter: (item: RiskActionRow) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment')),
      categoryGetter: (item: RiskActionRow) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    {
      id: 'actionFriendlyId',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('columns.id')),
      categoryGetter: (item: RiskActionRow) => item.ActionFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionFriendlyId'),
    },
    {
      id: 'actionTitle',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.title')),
      categoryGetter: (item: RiskActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
    {
      id: 'actionStatus',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.status')),
      categoryGetter: (item: RiskActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.priority')),
      // Structured result mirrors the single-entity action source (RSP-5303):
      // the numeric `key` makes pies/legends sort Urgent → High → Normal → Low
      // rather than alphabetically, and the `label` keeps the display readable.
      categoryGetter: (item: RiskActionRow) => {
        if (item.ActionPriorityValue == null) {
          return UNRATED;
        }

        return {
          sortKey: `${item.ActionPriorityValue}`,
          key: item.ActionPriorityValue,
          label: item.ActionPriority || UNRATED,
        };
      },
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'actionDateDue',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.due_date')),
      categoryGetter: (item: RiskActionRow) =>
        item.ActionDateDue ? new Date(item.ActionDateDue) : new Date(0),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { DateDue: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActionDateDue'),
    },
    {
      id: 'actionOwner',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.owner')),
      categoryGetter: (item: RiskActionRow) =>
        item.ActionOwners.map((owner) => ({
          key: owner.id,
          label: owner.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ActionOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'actionOverdue',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.overdue')),
      categoryGetter: (item: RiskActionRow) => item.ActionOverdue,
      clickthroughFilter: defaultClickthroughFilter('ActionOverdue'),
    },
  ],
});
